import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Badge,
  Button,
} from "react-bootstrap";
import {
  IconBrandWhatsapp,
  IconShieldCheck,
  IconRobot,
} from "@tabler/icons-react";
import TokenValidation from "./components/TokenValidation";
import WhatsAppConnectionFlow from "./components/WhatsAppConnectionFlow";
import usePublicConnection from "./hooks/usePublicConnection";
import "./styles/whatsapp-connect.css";

const VALIDATION_STATES = {
  TOKEN_VALIDATION: "TOKEN_VALIDATION",
  CONNECTION_FLOW: "CONNECTION_FLOW",
  ERROR: "ERROR",
};

const WhatsAppPublicConnect = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const {
    isLoading,
    error,
    currentState,
    setCurrentState,
    validatedData,
    handleTokenValidation,
    handleConnectionRequest,
    handleQrCodeGeneration,
    handleStatusCheck,
    handleConnectionSuccess,
    clearSession,
  } = usePublicConnection();

  const [validationError, setValidationError] = useState("");
  const [turnstileResetTrigger, setTurnstileResetTrigger] = useState(0);

  // Limpar estados ao montar componente
  useEffect(() => {
    clearSession();
    setCurrentState(VALIDATION_STATES.TOKEN_VALIDATION);
    setValidationError("");
  }, [token, clearSession, setCurrentState]);

  // Validar token da URL
  useEffect(() => {
    if (!token) {
      setCurrentState(VALIDATION_STATES.ERROR);
      return;
    }

    if (token.length < 6 || token.length > 8) {
      setCurrentState(VALIDATION_STATES.ERROR);
      return;
    }
  }, [token, setCurrentState]);

  // Função para tratar sucesso na validação do token
  const handleValidationSuccess = async (
    validatedTokenData,
    turnstileToken
  ) => {
    setValidationError("");

    try {
      const result = await handleTokenValidation(
        token,
        validatedTokenData,
        turnstileToken
      );

      if (result.success) {
        setCurrentState(VALIDATION_STATES.CONNECTION_FLOW);
      } else {
        // Verificar se é erro do Turnstile
        const isTurnstileError =
          result.errorCode === "TURNSTILE_FAILED" ||
          result.error?.includes("Validação de segurança falhou");

        if (isTurnstileError) {
          // Reset Turnstile
          setTurnstileResetTrigger((prev) => prev + 1);
          setValidationError(
            "Validação de segurança expirou. Por favor, tente novamente."
          );
        } else {
          setValidationError(result.error || "Erro na validação do token");
        }
      }
    } catch (error) {
      console.error("Erro na validação:", error);
      setValidationError("Erro inesperado na validação. Tente novamente.");
    }
  };

  // Função para voltar para validação
  const handleBackToValidation = () => {
    clearSession();
    setCurrentState(VALIDATION_STATES.TOKEN_VALIDATION);
    setValidationError("");
    setTurnstileResetTrigger((prev) => prev + 1);
  };

  // Renderizar conteúdo baseado no estado atual
  const renderContent = () => {
    switch (currentState) {
      case VALIDATION_STATES.TOKEN_VALIDATION:
        return (
          <TokenValidation
            token={token}
            onValidationSuccess={handleValidationSuccess}
            isLoading={isLoading}
            error={validationError || error}
            turnstileResetTrigger={turnstileResetTrigger}
          />
        );

      case VALIDATION_STATES.CONNECTION_FLOW:
        return (
          <WhatsAppConnectionFlow
            validatedData={validatedData}
            onConnectionSuccess={handleConnectionSuccess}
            onBack={handleBackToValidation}
            isLoading={isLoading}
            error={error}
            handleConnectionRequest={handleConnectionRequest}
            handleQrCodeGeneration={handleQrCodeGeneration}
            handleStatusCheck={handleStatusCheck}
          />
        );

      case VALIDATION_STATES.ERROR:
        return (
          <Container className="py-5">
            <Row className="justify-content-center">
              <Col lg={6} md={8} sm={10}>
                <div className="public-error-container">
                  <Card className="border-0 shadow-lg">
                    <Card.Body className="p-5 text-center">
                      <div
                        className="mx-auto mb-4 rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "80px",
                          height: "80px",
                          background: "#dc3545",
                          color: "white",
                        }}
                      >
                        <IconShieldCheck size={40} />
                      </div>

                      <h3 className="fw-bold text-danger mb-3">
                        Token Inválido
                      </h3>

                      <Alert variant="danger" className="text-start mb-4">
                        O token fornecido não é válido ou está malformado.
                        Verifique se o link foi copiado corretamente.
                      </Alert>

                      <div className="d-flex flex-column gap-3">
                        <Button
                          variant="outline-primary"
                          onClick={() => window.location.reload()}
                          style={{
                            borderRadius: "12px",
                            padding: "12px 24px",
                            fontWeight: "600",
                          }}
                        >
                          Recarregar Página
                        </Button>
                      </div>

                      <div className="text-center mt-4">
                        <small className="text-muted">
                          Entre em contato com o responsável para obter um novo
                          link de conexão
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          </Container>
        );

      default:
        return null;
    }
  };

  return (
    <div className="whatsapp-connect-wrapper">
      {/* Conteúdo Principal */}
      <div className="public-content">{renderContent()}</div>
    </div>
  );
};

export default WhatsAppPublicConnect;
